/* eslint-disable react/prop-types */
/**
 * ID79/ID80 — click-a-cell pickers for the watchlist risk details.
 *  - PlausibilityPickerDialog: shows the Plausibility (Time horizon & velocity) table; click a rating.
 *  - ConsequencePickerDialog: shows the Consequence table (types × levels); click a cell to set both.
 */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const HEADER_BG = "rgba(82,0,155,0.08)";
const SELECTED_BG = "rgba(82,0,155,0.18)";

// ID172 — `mode` selects the column layout. onSelect receives the whole row so the caller can read
// timeframe/velocity (timeframe_velocity mode) or the band name (likelihood mode).
export function PlausibilityPickerDialog({
  open,
  onClose,
  rows = [],
  value,
  onSelect,
  mode = "timeframe_velocity",
}) {
  const isLikelihood = mode === "likelihood";
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: "#52009b", fontWeight: 700 }}>
        {isLikelihood ? "Select Likelihood" : "Select Plausibility"}
      </DialogTitle>
      <DialogContent dividers>
        {rows.length === 0 ? (
          <MDTypography variant="body2" color="text.secondary">
            No {isLikelihood ? "likelihood bands" : "plausibility table"} configured. Set it up
            under Appetite Settings → Risk Evaluation Criteria.
          </MDTypography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: HEADER_BG }}>
                {isLikelihood ? (
                  <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>Likelihood</TableCell>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>Timeframe</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>Velocity</TableCell>
                  </>
                )}
                <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const selected = value && r.rating === value;
                return (
                  <TableRow
                    key={r.id || r.rating}
                    hover
                    onClick={() => {
                      onSelect(r);
                      onClose();
                    }}
                    sx={{ cursor: "pointer", bgcolor: selected ? SELECTED_BG : undefined }}
                  >
                    {isLikelihood ? (
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {r.rating}
                      </TableCell>
                    ) : (
                      <>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          {r.timeframe || "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          {r.velocity || "—"}
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontSize: "0.8rem", color: "#555" }}>
                      {r.description || "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={onClose}>Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}

export function ConsequencePickerDialog({
  open,
  onClose,
  levels = [],
  rows = [],
  typeValue,
  levelValue,
  onSelect,
}) {
  const cellText = (row, levelId) => {
    const c = (row.cells || []).find((x) => x.levelId === levelId);
    return c ? c.text : "";
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ color: "#52009b", fontWeight: 700 }}>
        Select Consequence Type &amp; Level
      </DialogTitle>
      <DialogContent dividers>
        {levels.length === 0 || rows.length === 0 ? (
          <MDTypography variant="body2" color="text.secondary">
            No consequence table configured. Set it up under Appetite Settings → Risk Evaluation
            Criteria.
          </MDTypography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            {/* Fixed layout → the Type column gets a set width and the Level columns share the rest
                evenly, so long labels wrap instead of stretching one column off-screen. */}
            <Table size="small" sx={{ minWidth: 720, tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow sx={{ bgcolor: HEADER_BG }}>
                  <TableCell
                    sx={{ fontWeight: 700, color: "#52009b", width: 130, wordBreak: "break-word" }}
                  >
                    Type \ Level
                  </TableCell>
                  {levels.map((l) => (
                    <TableCell
                      key={l.id}
                      sx={{ fontWeight: 700, color: "#52009b", wordBreak: "break-word" }}
                    >
                      {l.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id || row.label}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 130,
                        wordBreak: "break-word",
                        verticalAlign: "top",
                      }}
                    >
                      {row.label}
                    </TableCell>
                    {levels.map((l) => {
                      const selected = row.label === typeValue && l.label === levelValue;
                      return (
                        <TableCell
                          key={l.id}
                          onClick={() => {
                            onSelect(row.label, l.label);
                            onClose();
                          }}
                          sx={{
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            verticalAlign: "top",
                            wordBreak: "break-word",
                            bgcolor: selected ? SELECTED_BG : undefined,
                            border: selected ? "2px solid #52009b" : "1px solid rgba(0,0,0,0.08)",
                            "&:hover": { bgcolor: "rgba(82,0,155,0.06)" },
                          }}
                        >
                          {cellText(row, l.id) || "—"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={onClose}>Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}
